import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import type { IRange } from "app/pages/sales-intelligence/types/range";

import type { CrmFieldsFilterDtoDescription } from "../../crm-integration/types/filter-options";
import type {
    IIndustryCodesFilterDto,
    ILegacyIndustryFilterDto,
    IndustryTaxonomyFilterDto,
} from "../../industry/types/filter-values";
import type { TechnologiesFilterDto } from "../../technology/types/filter-values";

import type {
    AdChannel,
    AudienceAge,
    AudienceGender,
    BusinessModel,
    EmployeeHeadcount,
    KeywordsTrafficSource,
    TopLevelDomainOption,
    WebsiteType,
} from "./enums";
import type { LeadPriorityScore } from "./lead-priority";

export interface IWebsiteTypeFilterDto {
    readonly types?: readonly WebsiteType[];
    readonly inclusion?: InclusionOption;
}

export interface ITopLevelDomainsFilterDto {
    readonly type?: TopLevelDomainOption;
    readonly values?: readonly string[];
}

export interface IIncomingTrafficFilterDto {
    readonly monthlyVisits?: Partial<IRange<number>>;
    readonly mobileWebVisitsShare?: Partial<IRange<number>>;
    readonly uniqueVisitors?: Partial<IRange<number>>;
    readonly totalPageViews?: Partial<IRange<number>>;
    readonly avgMonthlyVisits?: Partial<IRange<number>>;
}

export interface IEngagementFilterDto {
    readonly bounceRate?: Partial<IRange<number>>;
    readonly avgPagesPerVisit?: Partial<IRange<number>>;
    readonly avgVisitDuration?: Partial<IRange<number>>;
    readonly avgVisitsPerUser?: Partial<IRange<number>>;
}

export interface IMarketingChannelsFilterDto {
    readonly directShare?: Partial<IRange<number>>;
    readonly emailShare?: Partial<IRange<number>>;
    readonly referralsShare?: Partial<IRange<number>>;
    readonly socialShare?: Partial<IRange<number>>;
    readonly organicSocialShare?: Partial<IRange<number>>;
    readonly paidSocialShare?: Partial<IRange<number>>;
    readonly genAiShare?: Partial<IRange<number>>;
    readonly affiliateShare?: Partial<IRange<number>>;
    readonly organicSearchShare?: Partial<IRange<number>>;
    readonly paidSearchShare?: Partial<IRange<number>>;
    readonly searchShare?: Partial<IRange<number>>;
    readonly displayAdsShare?: Partial<IRange<number>>;
}

export interface IInternationalVisitsFilterDto {
    readonly internationalCountry?: number;
    readonly internationalVisits?: Partial<IRange<number>>;
    readonly internationalVisitsShare?: Partial<IRange<number>>;
}

export interface ICompanyHeadquarterFilterDto {
    readonly inclusion?: InclusionOption;
    readonly codes?: readonly number[];
    readonly regions?: readonly string[];
    readonly zip?: readonly string[];
}

export type VisitsFromFilterDto = readonly number[];

export type IKeywordsFilterDto = {
    readonly keyword?: string;
    readonly trafficSource?: KeywordsTrafficSource;
};

export interface IDisplayAdsFormatFilterDto {
    readonly bannerShare?: Partial<IRange<number>>;
    readonly videoShare?: Partial<IRange<number>>;
    readonly nativeShare?: Partial<IRange<number>>;
}

export interface IAdSpendChannelsFilterDto {
    readonly searchChannelShare?: Partial<IRange<number>>;
    readonly displayChannelShare?: Partial<IRange<number>>;
}

export interface IAdSpendVolumeFilterDto {
    readonly channel?: AdChannel;
    readonly spend?: Partial<IRange<number>>;
    readonly activeCampaigns?: Partial<IRange<number>>;
    readonly adsNumber?: Partial<IRange<number>>;
    readonly mobileShare?: Partial<IRange<number>>;
}

export interface ISiteTagsDto {
    readonly inclusion: InclusionOption;
    readonly values: readonly string[];
}

export const crmScopeOptionMap = {
    netNew: "netNew",
    inCrm: "inCrm",
    both: "both",
} as const;

export type CrmScopeOption = (typeof crmScopeOptionMap)[keyof typeof crmScopeOptionMap];

export interface ILeadsSearchLeadsFiltersDto extends CrmFieldsFilterDtoDescription {
    readonly crmScope: CrmScopeOption;
    readonly countries: VisitsFromFilterDto;
    readonly suppressionListIds: readonly number[];
    readonly staticListsToExclude: readonly string[];
    readonly searchText: string;
    readonly websiteType: IWebsiteTypeFilterDto;
    readonly topLevelDomain: ITopLevelDomainsFilterDto;
    readonly annualRevenue: Partial<IRange<number>>;
    readonly onlineRevenue: Partial<IRange<number>>;
    readonly conversionRate: Partial<IRange<number>>;
    readonly monthlyAverageTransactions: Partial<IRange<number>>;
    readonly totalFundingAmount: Partial<IRange<number>>;
    readonly usdSpend: Partial<IRange<number>>;
    readonly incomingTraffic: IIncomingTrafficFilterDto;
    readonly engagement: IEngagementFilterDto;
    readonly marketingChannels: IMarketingChannelsFilterDto;
    readonly audienceAgeGroups: readonly AudienceAge[];
    readonly audienceGenderSplits: readonly AudienceGender[];
    readonly businessModels: readonly BusinessModel[];
    readonly leadScores: readonly LeadPriorityScore[];
    readonly employeeHeadcount: readonly EmployeeHeadcount[];
    readonly topGeoCountries: readonly number[];
    readonly internationalFilters: IInternationalVisitsFilterDto;
    /** @deprecated The legacy 2-level classification. Coexists with `industryTaxonomy` until sunset (SI-8780). */
    readonly industryFilter: ILegacyIndustryFilterDto;
    readonly industryCodesFilter: IIndustryCodesFilterDto;
    readonly industryTaxonomy: IndustryTaxonomyFilterDto;
    readonly siteTags: ISiteTagsDto;
    readonly technologies: readonly TechnologiesFilterDto[];
    readonly companyHeadquarter: ICompanyHeadquarterFilterDto;
    readonly keywords: IKeywordsFilterDto;
    readonly adSpendVolume: IAdSpendVolumeFilterDto;
    readonly adSpendChannels: IAdSpendChannelsFilterDto;
    readonly displayAdsFormat: IDisplayAdsFormatFilterDto;
}
